import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/components/auth/AuthProvider";
import { useToast } from "./use-toast";
import { WorkflowStep } from "@/types/workflow";

export function useWorkflowDocuments(step: WorkflowStep | null) {
  const { user } = useAuth();
  const { toast } = useToast();

  return useQuery({
    queryKey: ['workflow-document', step?.case_id, step?.title],
    queryFn: async () => {
      if (!step?.case_id || !step?.title || !user?.id) {
        console.log('Missing required parameters for workflow document fetch:', {
          caseId: step?.case_id,
          stepTitle: step?.title,
          userId: user?.id
        });
        return null;
      }

      try {
        // First verify the session is valid
        const { data: { session }, error: sessionError } = await supabase.auth.getSession();
        if (sessionError || !session) {
          console.error('Session error:', sessionError);
          toast({
            variant: "destructive",
            title: "Autentiseringsfeil",
            description: "Vennligst logg inn på nytt.",
          });
          throw sessionError;
        }

        console.log('Fetching workflow document:', {
          caseId: step.case_id,
          stepTitle: step.title,
          userId: user.id
        });

        // First try to find workflow-specific document
        const { data: workflowDoc, error: workflowError } = await supabase
          .from('documents')
          .select()
          .eq('case_id', step.case_id)
          .eq('title', step.title)
          .eq('user_id', user.id)
          .eq('document_type', 'workflow_generated')
          .maybeSingle();

        if (workflowError) {
          console.error('Error fetching workflow document:', workflowError);
          throw workflowError;
        }

        if (workflowDoc) {
          console.log('Found workflow document:', workflowDoc);
          return workflowDoc;
        }

        // If no workflow document found, try to find a general document
        const { data: generalDoc, error: generalError } = await supabase
          .from('documents')
          .select()
          .eq('user_id', user.id)
          .eq('title', step.title)
          .eq('category', step.title)
          .maybeSingle();

        if (generalError) {
          console.error('Error fetching general document:', generalError);
          throw generalError;
        }

        console.log('Found general document:', generalDoc);
        return generalDoc;

      } catch (error: any) {
        console.error('Error in workflow document fetch:', error);
        
        // Check if it's a network error
        if (error instanceof TypeError && error.message === 'Failed to fetch') {
          toast({
            variant: "destructive",
            title: "Nettverksfeil",
            description: "Kunne ikke koble til serveren. Sjekk internettforbindelsen din."
          });
        } else {
          toast({
            variant: "destructive",
            title: "Feil ved henting av dokument",
            description: "Kunne ikke hente dokumentet. Vennligst prøv igjen."
          });
        }
        
        throw error;
      }
    },
    enabled: !!step?.case_id && !!step?.title && !!user?.id,
    retry: (failureCount, error) => {
      // Retry up to 3 times for network errors
      if (error instanceof TypeError && error.message === 'Failed to fetch') {
        return failureCount < 3;
      }
      // Don't retry for other errors
      return false;
    },
    retryDelay: attemptIndex => Math.min(1000 * 2 ** attemptIndex, 10000), // Exponential backoff
    refetchOnWindowFocus: false,
    staleTime: 30000 // Cache results for 30 seconds
  });
}